import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Button, ClickAwayListener, Divider, Fade, InputAdornment, MenuItem, MenuList, Popper, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { DashboardContext } from '../../../contexts/DashboardContext/DashboardProvider';
import { DashboardsContext } from '../../../contexts/DashboardsContext/DashboardsProvider';
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import { createWorkspaceFunc, updateDashboardFunc } from '../../../lib/helper';
import { scrollbarStyle } from '../../../shared/dashboard';
import { Dashboard, DealSource, RefreshDataEnum, Workspace } from "../../../types/files";
import { FallbackLoading } from '../../templates/loader';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 'max-content',
        minWidth: 200,
        maxWidth: 400,
        height: 'fit-content',
        marginTop: 16,
        padding: '12px 16px',
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        maxWidth: '30ch',
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    menu: {
        width: '100%',
        padding: 4,
        "&.Mui-focusVisible": {
            background: 'transparent !important',
        },
        "&:hover": {
            background: `rgb(246, 251, 251, 1)`,
        }
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 24,
        height: 24,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            padding: '0 8px !important',
            borderRadius: 24,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    addButton: {
        minWidth: 60,
        height: 28,
        borderRadius: 16,
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        textTransform: 'none',
    },
}));

const SelectDealSourceModal: React.FC<{
    open: boolean,
    dashboard: Dashboard,
    anchorElement?: HTMLElement|null,
    onClose: () => void,
}> = ({ open, dashboard, anchorElement, onClose }) => {
    const classes = useStyles();
    const { workspaces, setWorkspaces } = useContext(WorkspacesContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { setDashboard } = useContext(DashboardContext);
    const { dashboardSource } = useBackwardsCompatible();

    const [loading, setLoading] = useState<boolean>(false);
    const [userEmail, setUserEmail] = useState<string>('');
    const [sources, setSources] = useState<(DealSource|undefined)[]>([]);
    const userEmailRef = useRef<TextFieldProps>(null);

    const debouncedName = useDebouncedCallback((name) => {
        setUserEmail(name);
    }, 100, { maxWait: 200 });

    const options = useMemo(() =>
        workspaces.filter(ws => !ws?.isShared).sort((wA, wB) => wA.name.localeCompare(wB.name))
    , [workspaces]);

    const checkSelected = useCallback((source: DealSource) => {
        const dealSource = dashboardSource(dashboard);

        return (dealSource?.name?.toLowerCase() === source?.name?.toLowerCase())
            && (dealSource?.email?.toLowerCase() === source?.email?.toLowerCase());
    }, [dashboard, dashboardSource]);

    const handleSave = useCallback(async (selection: DealSource|null) => {
        if (!dashboard)
            return;

        setLoading(true);

        const sourceName = selection?.name?.trim() || '';
        const sourceEmail = selection?.email?.trim() || '';
        const lookUpWorkspace = workspaces.find(ws =>
            (!!sourceName && (ws?.name?.toLowerCase() === sourceName?.toLowerCase())) ||
            (!!sourceEmail && (ws?.email?.toLowerCase() === sourceEmail?.toLowerCase()))
        );

        if (!lookUpWorkspace) {
            const newWorkspace: Workspace = await createWorkspaceFunc({ name: sourceName, email: sourceEmail, isPublic: false, isShared: false });

            setWorkspaces(prev => [...prev, newWorkspace]);
        }

        const updatedDashboard = {
            ...dashboard,
            dealSource: {name: sourceName, email: sourceEmail},
            source: sourceName ?? dashboard.dealSource?.name,
            refreshData: {
                ...dashboard.refreshData,
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Title,
            },
        };

        updateDashboardFunc(updatedDashboard).then(() => {
            setDashboard(updatedDashboard);
            setDashboards((prev) =>
                prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
            );
            setLoading(false);
        }).finally(onClose);
    //eslint-disable-next-line
    }, [dashboard, workspaces]);

    useEffect(() => {
        if (!!options.length) {
            setSources(options.filter((workspace) => !userEmail
                || workspace?.name?.toLowerCase().includes(userEmail?.toLowerCase())
                || workspace?.email?.toLowerCase().includes(userEmail?.toLowerCase())
            ));
        }
    }, [options, userEmail]);

    return (<>
        <Popper open={open} anchorEl={anchorElement} placement="bottom-start">
            <ClickAwayListener onClickAway={onClose}>
                <Stack className={classes.card} alignItems="flex-start" justifyContent="flex-start" width="100%">
                    <TextField autoFocus
                        variant="outlined"
                        className={classes.inputField}
                        placeholder={'Search deal source ...'}
                        onChange={(e) => debouncedName(e.target.value)}
                        inputRef={userEmailRef}
                        sx={{ width: '100%' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ color: 'rgb(163, 167, 172)' }}>
                                    <PersonOutlineOutlinedIcon height={24} width={24} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end" >
                                    {!!userEmail && (
                                        <Fade in timeout={1000}>
                                            <Button variant="contained"
                                                className={classes.addButton}
                                                onClick={() => handleSave(({ name: userEmail }) as DealSource)}
                                                disabled={!dashboard}> {'Add'} </Button>
                                        </Fade>
                                    )}
                                </InputAdornment>
                    )}} />
                    <MenuList sx={{ minWidth: 300 }}>
                        {loading ? (<FallbackLoading height="200px" />) : sources.length ? (
                            <Stack direction="column" sx={{ ...scrollbarStyle, maxHeight: 256, overflowY: 'auto' }} >
                                {sources.map((source, i) => (<>
                                    <MenuItem className={classes.menu}
                                        onClick={() => !checkSelected(source!) ? handleSave(source!) : undefined }
                                        key={'modify-deal-source-592-' + i}>
                                        <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start" width="100%">
                                            <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                                            <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between" width="100%">
                                                <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                                    {!!source?.name && (
                                                        <Typography className={classes.label}>
                                                            {source?.name}
                                                        </Typography>
                                                    )}
                                                    {!!source?.email && (
                                                        <Typography className={classNames(classes.label, classes.sublabel)}>
                                                            {source?.email}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {checkSelected(source!) && (
                                                    <Fade in timeout={500}>
                                                        <CheckCircleIcon className={classes.icon} />
                                                    </Fade>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </MenuItem>
                                    <Divider sx={{ width: '100%' }} />
                                </>))}
                            </Stack>
                        ) : !!userEmail && (
                            <Stack alignItems="center" justifyContent="center" width="100%" height={200}>
                                <Typography fontSize="1rem" color="#7e7e7e">
                                    {'No results found.'}
                                </Typography>
                            </Stack>
                        )}
                    </MenuList>
                </Stack>
            </ClickAwayListener>
        </Popper>
    </>);
}

export default SelectDealSourceModal;